import { FormInitializerReturnType, stringSchema, ValidationMessageKeys } from 'src/Configuration/validation'
import { CompanyForm, CompanyModel } from '../models'
import { CompanyContactData } from 'src/Libs/user/models'
import { useCountryCodesValidation } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { boolean, object, string } from 'yup'
import { PHONE_NUMBER_REGEX } from '@e3dc-react/shell/Elements/InputFields/defaults'
import { ONLY_DIGITS_REGEX } from 'src/Libs/installation-statement/hooks/validation/regex-patterns'
import { validateSapCustomer } from '../api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { modelsToForm } from '../transformers/models-to-form'

const MIN_STRING_LENGTH = 3
const MAX_NAME_LENGTH = 255
const MAX_ADDRESS_LENGTH = 50
const MIN_ZIP_LENGTH_MIN = 1
const MAX_ZIP_LENGTH_MAX = 10
const MAX_PHONE_LENGTH = 30

/**
 *  Initializes the form for editing company data
 * @param company CompanyModel
 * @param companyLocation CompanyContactData
 * @returns initialValues and validationSchema for company data form
 */
export const useCompanyFormInitializer = (
  company?: CompanyModel,
  companyLocation?: CompanyContactData
): FormInitializerReturnType<CompanyForm, CompanyForm> => {
  const { t } = useCustomTranslation()
  const stringSchemaWithTranslation = stringSchema(t)
  const authFetch = useAuthFetchWithResponse()

  const [validateCountryCode] = useCountryCodesValidation()
  const country = companyLocation?.Land ?? ''
  const validatedCountryCode = validateCountryCode(country) ? country : 'DEU'
  const initialValues = modelsToForm(validatedCountryCode, company, companyLocation)

  const validationSchema = object().shape({
    name: stringSchemaWithTranslation({ minLength: MIN_STRING_LENGTH, maxLength: MAX_NAME_LENGTH }).trim().required(),
    address: stringSchemaWithTranslation({ minLength: MIN_STRING_LENGTH, maxLength: MAX_ADDRESS_LENGTH }).trim().required(),
    city: stringSchemaWithTranslation({ minLength: MIN_STRING_LENGTH, maxLength: MAX_ADDRESS_LENGTH }).trim().required(),
    zipCode: stringSchemaWithTranslation({ minLength: MIN_ZIP_LENGTH_MIN, maxLength: MAX_ZIP_LENGTH_MAX }).trim().required(),
    phone: stringSchemaWithTranslation({
      minLength: MIN_STRING_LENGTH,
      maxLength: MAX_PHONE_LENGTH,
      regexp: { rule: PHONE_NUMBER_REGEX, errorMessage: ValidationMessageKeys.phoneFormat },
    })
      .required()
      .trim(),
    country: string().required(),
    email: string().email().required().trim(),
    fax: stringSchemaWithTranslation({
      minLength: MIN_STRING_LENGTH,
      maxLength: MAX_PHONE_LENGTH,
      regexp: { rule: PHONE_NUMBER_REGEX, errorMessage: ValidationMessageKeys.phoneFormat },
    }).trim(),
    sapCustomerId: stringSchemaWithTranslation({ regexp: { rule: ONLY_DIGITS_REGEX, errorMessage: ValidationMessageKeys.onlyDigits } })
      .test({
        message: t(ValidationMessageKeys.sapCustomerErrorKey),
        test: async value => {
          if (!value?.length) {
            return true
          }

          const validationResult = await validateSapCustomer(value, authFetch)

          return validationResult.value?.isValid || false
        },
      })
      .test({
        message: t(ValidationMessageKeys.sapCustomerNotUniqueErrorKey),
        test: async value => {
          if (!value?.length || value === initialValues.sapCustomerId) {
            return true
          }

          const validationResult = await validateSapCustomer(value, authFetch)

          return validationResult.value?.isUnique || false
        },
      })
      .trim(),
    partnerNumber: string(),
    ndaSubmit: string(),
    shopPermission: boolean().when('sapCustomerId', ([sapCustomerId], schema) => {
      return sapCustomerId && sapCustomerId.length > 0 ? schema.required() : schema.notRequired()
    }),
  })

  return { initialValues, validationSchema }
}
